import type { FC, ReactNode } from 'react';
import { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { AxiosResponse } from 'axios';

import { LANGUAGE } from 'src/constants/language';
import { ReportType } from 'src/constants/report';
import type {
  OnDemandReport,
  OnDemandReportCheckBox,
  OnDemandReportsDownloadContextValues,
} from 'src/types/report';
import useSurveySettings from 'src/hooks/useSurveySettings';
import { apiPaths } from 'src/services/api/apiPaths';
import HttpService from 'src/services/api/http';
import { useSection } from 'src/components/app/context/SectionContext';
import { useDashboardContext } from 'src/components/dashboard/DashboardContext';
import { errorMap } from 'src/components/dashboard/DashboardPage/components/ButtonReportModal/helper';
import useDashboardFilters from 'src/components/dashboard/hooks/useDashboardFilters';

export const OnDemandReportsDownloadContext =
  createContext<OnDemandReportsDownloadContextValues>({
    isLoading: false,
    selectedLanguage: 0,
    setSelectedLanguage: () => {},
    onDemandReports: [],
    setOnDemandReports: () => {},
    errorMsg: '',
  });

export const OnDemandReportsDownloadContextProvider: FC<{
  children?: ReactNode;
}> = ({ children }) => {
  const { t, i18n } = useTranslation('common');
  const { flatFilters } = useDashboardFilters();
  const { moduleId } = useSection();
  const { minRespondentAmount } = useSurveySettings();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const [onDemandReports, setOnDemandReports] = useState<
    OnDemandReportCheckBox[]
  >([]);
  const { dashboardConfig } = useDashboardContext();
  const [selectedLanguage, setSelectedLanguage] = useState<number>(LANGUAGE.EN);

  useEffect(() => {
    const newLang =
      dashboardConfig?.supported_languages?.find(
        (lang) => lang.abbreviation === i18n.language,
      )?.code ?? LANGUAGE.EN;
    setSelectedLanguage((prev) => (prev === newLang ? prev : newLang));
  }, [dashboardConfig?.supported_languages, i18n.language]);

  const supportedReportTypes = useMemo(
    () => dashboardConfig?.supported_report_types ?? [],
    [dashboardConfig],
  );

  useEffect(() => {
    // prevent irrelevant updates from async requests
    let active = true;

    if (Object.hasOwn(flatFilters, 'orgUnit')) {
      setIsLoading(true);
      HttpService.post(apiPaths.REPORTS.ON_DEMAND, {
        language: selectedLanguage,
        filter_data: flatFilters,
        module_id: moduleId,
      })
        .then(({ data }: AxiosResponse<OnDemandReport[]>) => {
          if (!active) {
            return;
          }

          setOnDemandReports(
            data
              .map((report) => ({
                ...report,
                checked: report.report_type === ReportType.MANAGER,
              }))
              .filter((report) =>
                supportedReportTypes.includes(report.report_type),
              ),
          );
        })
        .catch(({ response }) => {
          if (!active) {
            return;
          }

          if (response) {
            const msgString = errorMap[response.status];
            setErrorMsg(
              msgString
                ? t(msgString, { minRespondentAmount })
                : response.statusText,
            );
          } else {
            setErrorMsg(t('common:networkError'));
          }
          setOnDemandReports([]);
        })
        .finally(() => {
          if (active) {
            setIsLoading(false);
          }
        });
    }
    return () => {
      active = false;
      setErrorMsg('');
      setOnDemandReports([]);
      setIsLoading(false);
    };
  }, [selectedLanguage, flatFilters, moduleId, supportedReportTypes]);

  return (
    <OnDemandReportsDownloadContext.Provider
      value={{
        isLoading,
        selectedLanguage,
        setSelectedLanguage,
        onDemandReports,
        setOnDemandReports,
        errorMsg,
      }}
    >
      {children}
    </OnDemandReportsDownloadContext.Provider>
  );
};

const useOnDemandReportsDownloadContext = () =>
  useContext(OnDemandReportsDownloadContext);

export default useOnDemandReportsDownloadContext;
